import {
  and,
  ControlState,
  EnumCellProps,
  JsonSchema,
  RankedTester,
  rankWith,
  schemaMatches,
  uiTypeIs,
  WithClassname,
} from '@jsonforms/core';
import { Control } from '@jsonforms/react';
import { Autocomplete } from '@mantine/core';
import merge from 'lodash/merge';
import React, { useMemo } from 'react';
import { useDebouncedChange } from '../utils';
import { MantineInputControl } from './MantineInputControl';
import { WithInputProps } from '../mantine-controls/interfaces';
import { ControlProps } from '../../types/controlProps';
import { withJsonFormsControlProps } from '../utils/withJsonFormsPropsMappers';

const findEnumSchema = (schemas: JsonSchema[] | undefined) =>
  schemas?.find((s) => s.enum !== undefined && (s.type === 'string' || s.type === undefined));

const findTextSchema = (schemas: JsonSchema[] | undefined) =>
  schemas?.find((s) => s.type === 'string' && s.enum === undefined);

const MantineAutocompleteInputText = (props: EnumCellProps & WithClassname & WithInputProps) => {
  const { data, config, className, id, enabled, uischema, isValid, path, handleChange, schema, label } = props;

  const enumSchema = findEnumSchema(schema.anyOf);

  const appliedUiSchemaOptions = useMemo(() => merge({}, config, uischema.options), [config, uischema.options]);

  const [inputText, onChange] = useDebouncedChange(handleChange, '', data, path, (value) => value);

  const dataList = enumSchema?.enum?.map((optionValue) => optionValue);

  return (
    <Autocomplete
      data={dataList}
      value={inputText}
      //@ts-ignore
      onChange={onChange}
      className={className}
      id={id}
      label={label}
      disabled={!enabled}
      autoFocus={appliedUiSchemaOptions.focus}
      error={!isValid}
    />
  );
};

export class MantineAnyOfStringOrEnumControl extends Control<ControlProps, ControlState> {
  render() {
    return <MantineInputControl {...this.props} input={MantineAutocompleteInputText} />;
  }
}

const hasEnumAndText = (schemas: JsonSchema[] | undefined) => {
  const enumSchema = findEnumSchema(schemas);
  const stringSchema = findTextSchema(schemas);
  const remainingSchemas = schemas?.filter((s) => s !== enumSchema || s !== stringSchema);
  const wrongType = remainingSchemas?.find((s) => s.type && s.type !== 'string');
  return enumSchema && stringSchema && !wrongType;
};

const simpleAnyOf = and(
  uiTypeIs('Control'),
  schemaMatches((schema) => Object.prototype.hasOwnProperty.call(schema, 'anyOf') && !!hasEnumAndText(schema.anyOf))
);

export const mantineAnyOfStringOrEnumControlTester: RankedTester = rankWith(5, simpleAnyOf);

export default withJsonFormsControlProps(MantineAnyOfStringOrEnumControl);
